import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import {
  boardSwitcherSearchSharedState,
  boardSwitcherSharedState,
} from '@trello/board-switcher';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';
import { useSplitScreenSharedState } from '@trello/split-screen';

import { BoardSwitcherSpotlight } from './BoardSwitcherSpotlight';
import { RecentBoards } from './RecentBoards';
import { SearchField } from './SearchField';
import { SearchResults } from './SearchResults';
import { SelectedWorkspace } from './SelectedWorkspace';
import { StarredBoards } from './StarredBoards';
import { useBoardSwitcher } from './useBoardSwitcher';
import { useBoardSwitcherKeyboardNav } from './useBoardSwitcherKeyboardNav';
import { WorkspaceBoards } from './WorkspaceBoards';
import { WorkspaceTabs } from './WorkspaceTabs';

import styles from './BoardSwitcher.less';

interface BoardSwitcherProps {
  showDialogCallback?: () => void;
  hideDialogCallback?: () => void;
}

export const BoardSwitcher: FunctionComponent<BoardSwitcherProps> = ({
  showDialogCallback,
  hideDialogCallback,
}) => {
  const { workspaces, workspaceBoards, guestWorkspaceIds } = useBoardSwitcher();

  const [{ selectedWorkspaceId, mode }] = useSharedState(
    boardSwitcherSharedState,
  );

  const { value: isBoardSwitcherPanelEnabled } = useFeatureGate(
    'billplat_board_switcher_panel',
  );

  const { areMultiplePanelsOpen, configuration } = useSplitScreenSharedState();

  const query = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((value) => value.query, []),
  );
  const searchFieldRef = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((value) => value.searchFieldRef, []),
  );

  useBoardSwitcherKeyboardNav(searchFieldRef?.current ?? null);

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'boardSwitcherModal',
    });
  }, []);

  return (
    <BoardSwitcherSpotlight>
      <div
        className={cx({
          [styles.boardSwitcher]: true,
          [styles.panel]: mode === 'panel' && isBoardSwitcherPanelEnabled,
          [styles.singlePanel]:
            mode === 'panel' &&
            isBoardSwitcherPanelEnabled &&
            !areMultiplePanelsOpen &&
            configuration !== 'tabs',
        })}
        id="board-switcher"
      >
        <SearchField
          isPanel={isBoardSwitcherPanelEnabled && mode === 'panel'}
          showDialogCallback={showDialogCallback}
          hideDialogCallback={hideDialogCallback}
        />
        <WorkspaceTabs
          isPanel={isBoardSwitcherPanelEnabled && mode === 'panel'}
        />
        {query && (
          <div className={styles.scrollContainer}>
            <SearchResults />
          </div>
        )}

        {!query && (
          <>
            {selectedWorkspaceId ? (
              <SelectedWorkspace workspaceId={selectedWorkspaceId} />
            ) : (
              <div className={styles.scrollContainer}>
                <StarredBoards />
                <RecentBoards />

                {workspaces.map(({ id }) => (
                  <WorkspaceBoards
                    key={id}
                    workspaceId={id}
                    boardIds={workspaceBoards.get(id) ?? []}
                    showActions
                  />
                ))}

                {guestWorkspaceIds.map((id) => (
                  <WorkspaceBoards
                    key={id}
                    workspaceId={id}
                    boardIds={workspaceBoards.get(id) ?? []}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </BoardSwitcherSpotlight>
  );
};
